/**
 * The Material Design color palette as named hex values, for data display
 * (APP_PALETTE). Brand theme colors are in app_colors.ts.
 */
interface IColorVariants {
   base: string;
   lighten_5: string;
   lighten_4: string;
   lighten_3: string;
   lighten_2: string;
   lighten_1: string;
   darken_1: string;
   darken_2: string;
   darken_3: string;
   darken_4: string;
   accent_1?: string;
   accent_2?: string;
   accent_3?: string;
   accent_4?: string;
}

interface IColorSet {
   color: IColorVariants;
   text: IColorVariants;
}

interface IMdColors {
   red: IColorSet;
   pink: IColorSet;
   purple: IColorSet;
   deep_purple: IColorSet;
   indigo: IColorSet;
   blue: IColorSet;
   light_blue: IColorSet;
   cyan: IColorSet;
   teal: IColorSet;
   green: IColorSet;
   light_green: IColorSet;
   lime: IColorSet;
   yellow: IColorSet;
   amber: IColorSet;
   orange: IColorSet;
   deep_orange: IColorSet;
   brown: IColorSet;
   blue_grey: IColorSet;
   grey: IColorSet;
   neutral: {
      black: string;
      white: string;
      mid_blue_grey: string;
      light_blue_grey: string;
      text_std: string;
   };
}

export const MD_COLORS: IMdColors = {
   neutral: {
      black: '#181819',
      white: '#FFFFFF',
      mid_blue_grey: '#cfd8dc', //MD_COLORS.blue_grey.color.lighten_4,
      light_blue_grey: '#eceff1', //MD_COLORS.blue_grey.color.lighten_5,
      text_std: 'rgba(24, 24, 25, 0.87)',
   },
   red: {
      color: {
         base: '#F44336',
         lighten_5: '#FFEBEE',
         lighten_4: '#FFCDD2',
         lighten_3: '#EF9A9A',
         lighten_2: '#E57373',
         lighten_1: '#EF5350',
         darken_1: '#E53935',
         darken_2: '#D32F2F',
         darken_3: '#C62828',
         darken_4: '#B71C1C',
         accent_1: '#FF8A80',
         accent_2: '#FF5252',
         accent_3: '#FF1744',
         accent_4: '#D50000',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   pink: {
      color: {
         base: '#e91e63',
         lighten_5: '#fce4ec',
         lighten_4: '#f8bbd0',
         lighten_3: '#f48fb1',
         lighten_2: '#f06292',
         lighten_1: '#ec407a',
         darken_1: '#d81b60',
         darken_2: '#c2185b',
         darken_3: '#ad1457',
         darken_4: '#880e4f',
         accent_1: '#ff80ab',
         accent_2: '#ff4081',
         accent_3: '#f50057',
         accent_4: '#c51162',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#FFFFFF',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   purple: {
      color: {
         base: '#9c27b0',
         lighten_5: '#f3e5f5',
         lighten_4: '#e1bee7',
         lighten_3: '#ce93d8',
         lighten_2: '#ba68c8',
         lighten_1: '#ab47bc',
         darken_1: '#8e24aa',
         darken_2: '#7b1fa2',
         darken_3: '#6a1b9a',
         darken_4: '#4a148c',
         accent_1: '#ea80fc',
         accent_2: '#e040fb',
         accent_3: '#d500f9',
         accent_4: '#aa00ff',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#FFFFFF',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   deep_purple: {
      color: {
         base: '#673ab7',
         lighten_5: '#ede7f6',
         lighten_4: '#d1c4e9',
         lighten_3: '#b39ddb',
         lighten_2: '#9575cd',
         lighten_1: '#7e57c2',
         darken_1: '#5e35b1',
         darken_2: '#512da8',
         darken_3: '#4527a0',
         darken_4: '#311b92',
         accent_1: '#b388ff',
         accent_2: '#7c4dff',
         accent_3: '#651fff',
         accent_4: '#6200ea',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#FFFFFF',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   indigo: {
      color: {
         base: '#3f51b5',
         lighten_5: '#e8eaf6',
         lighten_4: '#c5cae9',
         lighten_3: '#9fa8da',
         lighten_2: '#7986cb',
         lighten_1: '#5c6bc0',
         darken_1: '#3949ab',
         darken_2: '#303f9f',
         darken_3: '#283593',
         darken_4: '#1a237e',
         accent_1: '#8c9eff',
         accent_2: '#536dfe',
         accent_3: '#3d5afe',
         accent_4: '#304ffe',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#FFFFFF',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   blue: {
      color: {
         base: '#2196F3',
         lighten_5: '#E3F2FD',
         lighten_4: '#BBDEFB',
         lighten_3: '#90CAF9',
         lighten_2: '#64B5F6',
         lighten_1: '#42A5F5',
         darken_1: '#1E88E5',
         darken_2: '#1976D2',
         darken_3: '#1565C0',
         darken_4: '#0D47A1',
         accent_1: '#82B1FF',
         accent_2: '#448AFF',
         accent_3: '#2979FF',
         accent_4: '#2962FF',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#000000',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   light_blue: {
      color: {
         base: '#03a9f4',
         lighten_5: '#e1f5fe',
         lighten_4: '#b3e5fc',
         lighten_3: '#81d4fa',
         lighten_2: '#4fc3f7',
         lighten_1: '#29b6f6',
         darken_1: '#039be5',
         darken_2: '#0288d1',
         darken_3: '#0277bd',
         darken_4: '#01579b',
         accent_1: '#80d8ff',
         accent_2: '#40c4ff',
         accent_3: '#00b0ff',
         accent_4: '#0091ea',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   cyan: {
      color: {
         base: '#00bcd4',
         lighten_5: '#e0f7fa',
         lighten_4: '#b2ebf2',
         lighten_3: '#80deea',
         lighten_2: '#4dd0e1',
         lighten_1: '#26c6da',
         darken_1: '#00acc1',
         darken_2: '#0097a7',
         darken_3: '#00838f',
         darken_4: '#006064',
         accent_1: '#84ffff',
         accent_2: '#18ffff',
         accent_3: '#00e5ff',
         accent_4: '#00b8d4',
      },
      text: {
         base: '#000000',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#000000',
         lighten_1: '#000000',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#000000',
         accent_3: '#000000',
         accent_4: '#FFFFFF',
      },
   },
   teal: {
      color: {
         base: '#009688',
         lighten_5: '#e0f2f1',
         lighten_4: '#b2dfdb',
         lighten_3: '#80cbc4',
         lighten_2: '#4db6ac',
         lighten_1: '#26a69a',
         darken_1: '#00897b',
         darken_2: '#00796b',
         darken_3: '#00695c',
         darken_4: '#004d40',
         accent_1: '#a7ffeb',
         accent_2: '#64ffda',
         accent_3: '#1de9b6',
         accent_4: '#00bfa5',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   green: {
      color: {
         base: '#4caf50',
         lighten_5: '#e8f5e9',
         lighten_4: '#c8e6c9',
         lighten_3: '#a5d6a7',
         lighten_2: '#81c784',
         lighten_1: '#66bb6a',
         darken_1: '#43a047',
         darken_2: '#388e3c',
         darken_3: '#2e7d32',
         darken_4: '#1b5e20',
         accent_1: '#b9f6ca',
         accent_2: '#69f0ae',
         accent_3: '#00e676',
         accent_4: '#00c853',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   light_green: {
      color: {
         base: '#8bc34a',
         lighten_5: '#f1f8e9',
         lighten_4: '#dcedc8',
         lighten_3: '#c5e1a5',
         lighten_2: '#aed581',
         lighten_1: '#9ccc65',
         darken_1: '#7cb342',
         darken_2: '#689f38',
         darken_3: '#558b2f',
         darken_4: '#33691e',
         accent_1: '#ccff90',
         accent_2: '#b2ff59',
         accent_3: '#76ff03',
         accent_4: '#64dd17',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   lime: {
      color: {
         base: '#cddc39',
         lighten_5: '#f9fbe7',
         lighten_4: '#f0f4c3',
         lighten_3: '#e6ee9c',
         lighten_2: '#dce775',
         lighten_1: '#d4e157',
         darken_1: '#c0ca33',
         darken_2: '#afb42b',
         darken_3: '#9e9d24',
         darken_4: '#827717',
         accent_1: '#f4ff81',
         accent_2: '#eeff41',
         accent_3: '#c6ff00',
         accent_4: '#aeea00',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   yellow: {
      color: {
         base: '#ffeb3b',
         lighten_5: '#fffde7',
         lighten_4: '#fff9c4',
         lighten_3: '#fff59d',
         lighten_2: '#fff176',
         lighten_1: '#ffee58',
         darken_1: '#fdd835',
         darken_2: '#fbc02d',
         darken_3: '#f9a825',
         darken_4: '#f57f17',
         accent_1: '#ffff8d',
         accent_2: '#ffff00',
         accent_3: '#ffea00',
         accent_4: '#ffd600',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   amber: {
      color: {
         base: '#ffc107',
         lighten_5: '#fff8e1',
         lighten_4: '#ffecb3',
         lighten_3: '#ffe082',
         lighten_2: '#ffd54f',
         lighten_1: '#ffca28',
         darken_1: '#ffb300',
         darken_2: '#ffa000',
         darken_3: '#ff8f00',
         darken_4: '#ff6f00',
         accent_1: '#ffe57f',
         accent_2: '#ffd740',
         accent_3: '#ffc400',
         accent_4: '#ffab00',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   orange: {
      color: {
         base: '#ff9800',
         lighten_5: '#fff3e0',
         lighten_4: '#ffe0b2',
         lighten_3: '#ffcc80',
         lighten_2: '#ffb74d',
         lighten_1: '#ffa726',
         darken_1: '#fb8c00',
         darken_2: '#f57c00',
         darken_3: '#ef6c00',
         darken_4: '#e65100',
         accent_1: '#ffd180',
         accent_2: '#ffab40',
         accent_3: '#ff9100',
         accent_4: '#ff6d00',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   deep_orange: {
      color: {
         base: '#ff5722',
         lighten_5: '#fbe9e7',
         lighten_4: '#ffccbc',
         lighten_3: '#ffab91',
         lighten_2: '#ff8a65',
         lighten_1: '#ff7043',
         darken_1: '#f4511e',
         darken_2: '#e64a19',
         darken_3: '#d84315',
         darken_4: '#bf360c',
         accent_1: '#ff9e80',
         accent_2: '#ff6e40',
         accent_3: '#ff3d00',
         accent_4: '#dd2c00',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
         accent_1: '#000000',
         accent_2: '#FFFFFF',
         accent_3: '#FFFFFF',
         accent_4: '#FFFFFF',
      },
   },
   brown: {
      color: {
         base: '#795548',
         lighten_5: '#efebe9',
         lighten_4: '#d7ccc8',
         lighten_3: '#bcaaa4',
         lighten_2: '#a1887f',
         lighten_1: '#8d6e63',
         darken_1: '#6d4c41',
         darken_2: '#5d4037',
         darken_3: '#4e342e',
         darken_4: '#3e2723',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
      },
   },
   blue_grey: {
      color: {
         base: '#607d8b',
         lighten_5: '#eceff1',
         lighten_4: '#cfd8dc',
         lighten_3: '#b0bec5',
         lighten_2: '#90a4ae',
         lighten_1: '#78909c',
         darken_1: '#546e7a',
         darken_2: '#455a64',
         darken_3: '#37474f',
         darken_4: '#263238',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#FFFFFF',
         lighten_1: '#FFFFFF',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
      },
   },
   grey: {
      color: {
         base: '#9E9E9E',
         lighten_5: '#FAFAFA',
         lighten_4: '#F5F5F5',
         lighten_3: '#EEEEEE',
         lighten_2: '#E0E0E0',
         lighten_1: '#BDBDBD',
         darken_1: '#757575',
         darken_2: '#616161',
         darken_3: '#424242',
         darken_4: '#212121',
      },
      text: {
         base: '#FFFFFF',
         lighten_5: '#000000',
         lighten_4: '#000000',
         lighten_3: '#000000',
         lighten_2: '#000000',
         lighten_1: '#000000',
         darken_1: '#FFFFFF',
         darken_2: '#FFFFFF',
         darken_3: '#FFFFFF',
         darken_4: '#FFFFFF',
      },
   },
} as const;
